import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { getSiteVisitStatus } from "../../../services/Enums/siteVisitStatusEnum";
import { getFieldTeamReportingStatus } from "../../../services/Enums/fieldTeamReportingStatusEnum";
import { months } from "../../../services/Enums/monthsAndYears";
import { formatDate } from "../../../services/helpFunctions/formatDateAndTime";
import { siteVisitPersonName } from "../SiteVisitColumns/siteVisitColumnOrder";

/* Site-visit exports as PDF, produced in the browser.

   The Excel export is a Hangfire job: the POST only queues work and a workbook
   turns up in the file manager minutes later. That is the right shape for a
   workbook with media zipped alongside it, and the wrong shape for "print me
   this table" — so PDF is built here, from data already on the client, and
   lands in the user's downloads straight away. No new dependency either: jspdf
   and html2canvas are the pair the maps and dashboard charts already export
   with.

   Two documents come out of this file:

   - exportSiteVisitsPdf draws the table as real PDF text — selectable,
     searchable, a few hundred KB for a thousand rows — instead of
     screenshotting a DOM table. The header repeats on every page, cells wrap,
     and the status columns keep the coloured dot the tables use.
   - exportNodeToPdf rasterises a DOM node, which is what the site-visit
     preview wants: there the ask is "the card exactly as I am looking at it",
     and that is a picture, not a table. */

/* ---- shared bits -------------------------------------------------------- */

const INK = "#1f2430";
const MUTED = "#6b7280";
const HAIRLINE = "#ebe7ea";
const ZEBRA = "#fcfbfc";
const HEAD_BG = "#f7f4f6";
const BRAND = "#aa1a5f";

const hexToRgb = (hex) => {
  const clean = String(hex || "").replace("#", "");
  const full =
    clean.length === 3
      ? clean
          .split("")
          .map((c) => c + c)
          .join("")
      : clean.slice(0, 6);
  const n = parseInt(full, 16);
  if (Number.isNaN(n)) return [107, 114, 128];
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

/* Status fills in the enums are written for CSS, so half of them arrive as
   "var(--Graph-Green-1, #06768E)" or "rgba(172, 12, 12, 0.777)". Pull the
   colour back out rather than shipping a second palette that can drift. */
const resolveFill = (fill) => {
  const value = String(fill || "");
  const hex = value.match(/#[0-9a-f]{3,8}/i);
  if (hex) return hexToRgb(hex[0]);
  const rgb = value.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  return hexToRgb(MUTED);
};

const setFill = (doc, colour) => doc.setFillColor(...hexToRgb(colour));
const setInk = (doc, colour) => doc.setTextColor(...hexToRgb(colour));
const setLine = (doc, colour) => doc.setDrawColor(...hexToRgb(colour));

const EMPTY = "—";

const asText = (value) =>
  value === null || value === undefined || value === "" ? EMPTY : String(value);

const dateText = (value) => {
  if (!value) return EMPTY;
  const out = formatDate(value);
  return out === "Invalid date" ? String(value) : out;
};

const timeStamp = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(
    d.getHours()
  )}${pad(d.getMinutes())}`;
};

const readableNow = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${formatDate(d)} at ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const slugify = (value) =>
  String(value || "export")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60) || "export";

/* ---- what a site-visit row looks like on paper -------------------------- */

/* Labels and order come from the canonical column table the site-visit grids
   already share, so a printed table reads in the same sequence as the one it
   was printed from. `weight` is a share of the usable width rather than a fixed
   size, so the same field set fits whatever page it is given. */
const FIELDS = {
  identifier: {
    label: "Site Visit Identifier",
    weight: 1.2,
    get: (r) => asText(r?.identifier),
  },
  name: {
    label: "Site Visit Name",
    weight: 1.6,
    get: (r) => asText(r?.name),
  },
  siteVisitStatus: {
    label: "Site Visit Status",
    weight: 1.25,
    dot: (r) => getSiteVisitStatus(r?.siteVisitStatus).fill,
    get: (r) => getSiteVisitStatus(r?.siteVisitStatus).text,
  },
  dateOfPlannedVisit: {
    label: "Date of Target Visit",
    weight: 1,
    get: (r) => dateText(r?.visit?.plannedVisit),
  },
  facility: {
    label: "Facility",
    weight: 1.3,
    get: (r) => asText(r?.facility?.name),
  },
  ip: {
    label: "IP",
    weight: 1.3,
    get: (r) => asText(r?.ipPlan?.implementingPartner?.name),
  },
  location: {
    label: "Location",
    weight: 0.9,
    get: (r) => asText(r?.locationDetails?.country),
  },
  state: {
    label: "Governate/State",
    weight: 1,
    get: (r) => asText(r?.locationDetails?.state),
  },
  cluster: {
    label: "Cluster",
    weight: 1,
    get: (r) => asText(r?.cluster?.name),
  },
  virtual: {
    label: "Virtual",
    weight: 0.5,
    get: (r) => (r?.isVirtual ? "Yes" : "No"),
  },
  tpmCoordinator: {
    label: "TPM Coordinator",
    weight: 1.2,
    get: (r) => asText(siteVisitPersonName(r?.logistic?.tpmCoordinator)),
  },
  team: {
    label: "Team",
    weight: 1.1,
    get: (r) => asText(r?.logistic?.team?.name),
  },
  actualDateOfVisit: {
    label: "Actual Date of Visit",
    weight: 1.15,
    get: (r) => {
      const dates = r?.tracking?.siteTrackingVisitDates || [];
      if (!dates.length) return EMPTY;
      const text = dates
        .map((d) => dateText(d?.visitDate))
        .filter((d) => d !== EMPTY)
        .join(", ");
      return text || EMPTY;
    },
  },
  reportingMonthYear: {
    label: "Reporting Month-Year",
    weight: 1.05,
    get: (r) => {
      const month = months[Number(r?.tracking?.reportingMonth)];
      const year = r?.tracking?.reportingYear;
      if (!month && !year) return EMPTY;
      return [month, year].filter(Boolean).join(" ");
    },
  },
  percentageCompleted: {
    label: "% Completed",
    weight: 0.7,
    get: (r) => {
      const value = r?.tracking?.percentageOfDataCollectionCompleted;
      if (value === null || value === undefined || value === "") return EMPTY;
      return String(value).includes("%") ? String(value) : `${value}%`;
    },
  },
  fieldTeamReportingStatus: {
    label: "Field Report Status",
    weight: 1.35,
    dot: (r) =>
      getFieldTeamReportingStatus(r?.tracking?.fieldTeamReportingStatus).fill,
    get: (r) =>
      r?.tracking?.fieldTeamReportingStatus
        ? getFieldTeamReportingStatus(r.tracking.fieldTeamReportingStatus).text
        : EMPTY,
  },
  dateOfReport: {
    label: "Date of Report",
    weight: 1,
    get: (r) => dateText(r?.tracking?.dateOfReport),
  },
  dateOfDebrief: {
    label: "Date of Debrief",
    weight: 1,
    get: (r) => dateText(r?.tracking?.dateOfDebrief),
  },
};

/* Two field sets, mirroring the "Key columns / All columns" toggle the tables
   themselves carry. The wide set gets a wider sheet rather than a smaller font
   — eighteen columns squeezed onto A4 is a document nobody can read. */
export const PDF_COLUMN_SETS = {
  key: {
    label: "Key columns",
    format: "a4",
    fields: [
      "identifier",
      "name",
      "siteVisitStatus",
      "dateOfPlannedVisit",
      "facility",
      "ip",
      "state",
      "tpmCoordinator",
      "team",
    ],
  },
  all: {
    label: "All columns",
    format: "a3",
    fields: [
      "identifier",
      "name",
      "siteVisitStatus",
      "dateOfPlannedVisit",
      "facility",
      "ip",
      "location",
      "state",
      "cluster",
      "virtual",
      "tpmCoordinator",
      "team",
      "actualDateOfVisit",
      "reportingMonthYear",
      "percentageCompleted",
      "fieldTeamReportingStatus",
      "dateOfReport",
      "dateOfDebrief",
    ],
  },
};

/* ---- the table document ------------------------------------------------- */

const MARGIN = 26;
const BODY_FONT = 7.2;
const HEAD_FONT = 6.6;
const LINE_H = 8.4;
const PAD_X = 5;
const PAD_Y = 5;
const MAX_LINES = 3;
const DOT_R = 2.1;
const DOT_GAP = 8;
const FOOTER_H = 22;

/* Weight decides a column's share of the page, but it cannot be the last word
   on width: splitTextToSize breaks a token that does not fit wherever it runs
   out of room, so a column narrower than the longest word in its own header
   prints "Governate/Stat" above "e". Every column is therefore raised to that
   floor first, and the difference is taken back proportionally from the columns
   that still have slack above theirs. The floor is capped so that one long
   label cannot eat the page. */
const FLOOR_CAP = 0.16;

const widestWord = (doc, label) =>
  String(label)
    .split(/\s+/)
    .reduce((max, word) => Math.max(max, doc.getTextWidth(word)), 0);

const distributeWidths = (doc, fields, usable) => {
  const totalWeight = fields.reduce((sum, field) => sum + field.weight, 0);
  const floors = fields.map((field) =>
    Math.min(widestWord(doc, field.label) + PAD_X * 2 + 1, usable * FLOOR_CAP)
  );

  let widths = fields.map((field) => (field.weight / totalWeight) * usable);
  let deficit = 0;
  widths = widths.map((width, i) => {
    if (width >= floors[i]) return width;
    deficit += floors[i] - width;
    return floors[i];
  });

  if (deficit > 0) {
    const slack = widths.map((width, i) => Math.max(0, width - floors[i]));
    const totalSlack = slack.reduce((sum, value) => sum + value, 0);
    if (totalSlack > 0) {
      const taken = Math.min(deficit, totalSlack);
      widths = widths.map(
        (width, i) => width - (slack[i] / totalSlack) * taken
      );
    }
  }

  return widths;
};

/* Draw the column header. Called once per page, so a table that runs to twelve
   pages is still readable on page twelve. */
const drawHead = (doc, cols, x0, y, width) => {
  const height =
    Math.max(...cols.map((c) => c.headLines.length), 1) * LINE_H + PAD_Y * 2;

  setFill(doc, HEAD_BG);
  doc.rect(x0, y, width, height, "F");
  setLine(doc, HAIRLINE);
  doc.setLineWidth(0.5);
  doc.line(x0, y + height, x0 + width, y + height);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(HEAD_FONT);
  setInk(doc, MUTED);

  let x = x0;
  cols.forEach((col) => {
    col.headLines.forEach((line, i) => {
      doc.text(line, x + PAD_X, y + PAD_Y + LINE_H * i + LINE_H - 2.4);
    });
    x += col.width;
  });

  return y + height;
};

const drawRow = (doc, cols, row, x0, y, width, zebra) => {
  if (zebra) {
    setFill(doc, ZEBRA);
    doc.rect(x0, y, width, row.height, "F");
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(BODY_FONT);

  let x = x0;
  cols.forEach((col, index) => {
    const cell = row.cells[index];
    const indent = cell.dot ? DOT_GAP : 0;

    if (cell.dot) {
      doc.setFillColor(cell.dot[0], cell.dot[1], cell.dot[2]);
      doc.circle(x + PAD_X + DOT_R, y + PAD_Y + LINE_H - 4.6, DOT_R, "F");
    }

    setInk(doc, cell.muted ? MUTED : INK);
    cell.lines.forEach((line, i) => {
      doc.text(line, x + PAD_X + indent, y + PAD_Y + LINE_H * i + LINE_H - 2.4);
    });
    x += col.width;
  });

  setLine(doc, HAIRLINE);
  doc.setLineWidth(0.4);
  doc.line(x0, y + row.height, x0 + width, y + row.height);

  return y + row.height;
};

const drawTitle = (doc, { title, subtitle, meta }, x0, y, width) => {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  setInk(doc, INK);
  doc.text(title, x0, y + 11);

  let cursor = y + 11;

  if (meta) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.6);
    setInk(doc, MUTED);
    doc.text(meta, x0 + width, y + 11, { align: "right" });
  }

  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.4);
    setInk(doc, MUTED);
    cursor += 12;
    doc.text(subtitle, x0, cursor);
  }

  setLine(doc, BRAND);
  doc.setLineWidth(1.1);
  doc.line(x0, cursor + 8, x0 + 34, cursor + 8);
  setLine(doc, HAIRLINE);
  doc.setLineWidth(0.5);
  doc.line(x0 + 34, cursor + 8, x0 + width, cursor + 8);

  return cursor + 20;
};

/* Page numbers are stamped in a second pass: "of n" is not knowable until the
   last row has been laid out. */
const stampFooters = (doc, label) => {
  const total = doc.getNumberOfPages();
  for (let page = 1; page <= total; page += 1) {
    doc.setPage(page);
    const w = doc.internal.pageSize.getWidth();
    const h = doc.internal.pageSize.getHeight();
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.2);
    setInk(doc, MUTED);
    doc.text(label, MARGIN, h - 12);
    doc.text(`Page ${page} of ${total}`, w - MARGIN, h - 12, {
      align: "right",
    });
  }
};

// Laying out a few thousand rows is a lot of synchronous text measuring. Hand
// the main thread back every so often so the button's spinner actually spins
// instead of the tab going white.
const YIELD_EVERY = 250;
const yieldToBrowser = () =>
  new Promise((resolve) => {
    setTimeout(resolve, 0);
  });

/**
 * Lay site-visit records out as a paginated PDF table.
 *
 * Split from the save step so the document can be built and inspected without a
 * browser — jspdf runs perfectly well in node, html2canvas is never touched on
 * this path, and a table this fiddly deserves to be checkable.
 *
 * @param rows      site-visit records exactly as getAllSiteVisits returns them
 * @param columnSet "key" (A4) or "all" (A3), matching the tables' own toggle
 * @param title     document heading, e.g. "Site visits — selected"
 * @param subtitle  one line under it; the scope line, usually
 */
export const buildSiteVisitsPdfDoc = async ({
  rows = [],
  columnSet = "key",
  title = "Site visits",
  subtitle = "",
} = {}) => {
  const set = PDF_COLUMN_SETS[columnSet] || PDF_COLUMN_SETS.key;
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: set.format,
    compress: true,
  });

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const usableW = pageW - MARGIN * 2;

  const chosen = set.fields.map((key) => FIELDS[key]).filter(Boolean);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(HEAD_FONT);
  const widths = distributeWidths(doc, chosen, usableW);
  const cols = chosen.map((field, index) => ({
    field,
    width: widths[index],
    headLines: doc.splitTextToSize(field.label, widths[index] - PAD_X * 2),
  }));

  /* Lay every row out before drawing anything: row height depends on how many
     lines the tallest cell wraps to, and pagination depends on row height. */
  doc.setFont("helvetica", "normal");
  doc.setFontSize(BODY_FONT);
  const laidOut = [];
  for (let index = 0; index < rows.length; index += 1) {
    const record = rows[index];
    const cells = cols.map((col) => {
      const value = col.field.get(record);
      const dot =
        col.field.dot && value !== EMPTY
          ? resolveFill(col.field.dot(record))
          : null;
      const room = col.width - PAD_X * 2 - (dot ? DOT_GAP : 0);
      let lines = doc.splitTextToSize(value, Math.max(room, 12));
      if (lines.length > MAX_LINES) {
        const last = String(lines[MAX_LINES - 1]);
        lines = lines.slice(0, MAX_LINES);
        lines[MAX_LINES - 1] = `${last.slice(0, Math.max(0, last.length - 1))}…`;
      }
      return { lines, dot, muted: value === EMPTY };
    });
    const height =
      Math.max(...cells.map((cell) => cell.lines.length), 1) * LINE_H +
      PAD_Y * 2;
    laidOut.push({ cells, height });

    if (index && index % YIELD_EVERY === 0) {
      // eslint-disable-next-line no-await-in-loop
      await yieldToBrowser();
    }
  }

  const count = rows.length;
  const bottom = pageH - MARGIN - FOOTER_H;

  let y = drawTitle(
    doc,
    {
      title,
      subtitle:
        subtitle ||
        `${count} site visit${count === 1 ? "" : "s"} · ${set.label}`,
      meta: `Generated ${readableNow()}`,
    },
    MARGIN,
    MARGIN,
    usableW
  );

  if (!count) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    setInk(doc, MUTED);
    doc.text("No site visits matched this export.", MARGIN, y + 14);
  } else {
    y = drawHead(doc, cols, MARGIN, y, usableW);

    laidOut.forEach((row, index) => {
      if (y + row.height > bottom) {
        doc.addPage();
        y = drawHead(doc, cols, MARGIN, MARGIN, usableW);
      }
      y = drawRow(doc, cols, row, MARGIN, y, usableW, index % 2 === 1);
    });
  }

  stampFooters(doc, title);

  return doc;
};

/**
 * Build the table and hand it to the browser as a download.
 *
 * @param fileName base name; the timestamp and extension are added here
 */
export const exportSiteVisitsPdf = async ({
  fileName = "site-visits",
  ...options
} = {}) => {
  const doc = await buildSiteVisitsPdfDoc(options);
  const name = `${slugify(fileName)}-${timeStamp()}.pdf`;
  doc.save(name);
  return { ok: true, fileName: name, count: options.rows?.length || 0 };
};

/* ---- the "copy of what I am looking at" document ------------------------- */

/* html2canvas sizes its canvas from the live element, then renders a clone. So
   anything the clone drops — the buttons and the Cancel row, in our case —
   still leaves its height behind as white space at the foot of the document,
   which on a three-page card is enough to buy a nearly empty fourth. Scan back
   up from the bottom for the last row with ink on it and cut there. Guarded:
   a canvas tainted by a cross-origin image throws on read, and an untrimmed
   PDF is better than no PDF. */
const TRIM_MARGIN = 0.008;

const trimTrailingBlank = (canvas) => {
  try {
    const { width, height } = canvas;
    if (!width || !height) return canvas;

    const scan = Math.ceil(height * 0.5);
    const top = height - scan;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const data = ctx.getImageData(0, top, width, scan).data;

    let lastInk = -1;
    for (let y = scan - 1; y >= 0 && lastInk < 0; y -= 1) {
      const row = y * width * 4;
      for (let x = 0; x < width; x += 4) {
        const i = row + x * 4;
        if (data[i] < 248 || data[i + 1] < 248 || data[i + 2] < 248) {
          lastInk = y;
          break;
        }
      }
    }

    const margin = Math.round(height * TRIM_MARGIN);
    const keep = Math.min(height, top + Math.max(lastInk, 0) + margin);
    if (height - keep < 8) return canvas;

    const trimmed = document.createElement("canvas");
    trimmed.width = width;
    trimmed.height = keep;
    const out = trimmed.getContext("2d");
    out.fillStyle = "#ffffff";
    out.fillRect(0, 0, width, keep);
    out.drawImage(canvas, 0, 0);
    return trimmed;
  } catch (error) {
    return canvas;
  }
};

/* Chrome refuses canvases past a few hundred megapixels, and a long site-visit
   card at scale 2 can get there. Drop the scale just far enough rather than
   failing. */
const MAX_CANVAS_AREA = 26000000;

const fittingScale = (node, requested) => {
  const width = node.scrollWidth || node.offsetWidth || 800;
  const height = node.scrollHeight || node.offsetHeight || 1000;
  const area = width * height;
  if (!area || area * requested * requested <= MAX_CANVAS_AREA) return requested;
  return Math.max(1, Math.sqrt(MAX_CANVAS_AREA / area));
};

/**
 * Rasterise a DOM node into a paginated PDF.
 *
 * Anything inside the node marked `data-pdf-hide` is dropped from the copy, and
 * anything marked `data-pdf-expand` has its height cap lifted — both applied to
 * html2canvas' cloned document, so the live modal never flickers.
 */
export const exportNodeToPdf = async ({
  node,
  fileName = "export",
  orientation = "portrait",
  scale = 2,
} = {}) => {
  if (!node) return { ok: false, error: "There is nothing to export yet." };

  try {
    const raw = await html2canvas(node, {
      scale: fittingScale(node, scale),
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
      onclone: (cloned) => {
        cloned.querySelectorAll("[data-pdf-hide]").forEach((el) => {
          if (el.parentNode) el.parentNode.removeChild(el);
        });
        cloned.querySelectorAll("[data-pdf-expand]").forEach((el) => {
          el.style.maxHeight = "none";
          el.style.overflow = "visible";
        });
      },
    });

    const canvas = trimTrailingBlank(raw);

    const doc = new jsPDF({
      orientation,
      unit: "pt",
      format: "a4",
      compress: true,
    });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const imgW = pageW - MARGIN * 2;
    const imgH = (canvas.height * imgW) / canvas.width;
    const usable = pageH - MARGIN * 2;
    const image = canvas.toDataURL("image/png");

    doc.addImage(image, "PNG", MARGIN, MARGIN, imgW, imgH, undefined, "FAST");
    let remaining = imgH - usable;

    while (remaining > 0) {
      doc.addPage();
      doc.addImage(
        image,
        "PNG",
        MARGIN,
        MARGIN - (imgH - remaining),
        imgW,
        imgH,
        undefined,
        "FAST"
      );
      remaining -= usable;
    }

    const name = `${slugify(fileName)}-${timeStamp()}.pdf`;
    doc.save(name);
    return { ok: true, fileName: name };
  } catch (error) {
    return {
      ok: false,
      error: "We couldn't build the PDF from this view. Please try again.",
    };
  }
};

export default exportSiteVisitsPdf;
